import { useState } from 'react';
import { Card, Button, NumberInput } from '@atoms/index';
import { cn } from '@/utils/cn';

interface StockUpdateModalProps {
  isOpen: boolean;
  productName: string;
  currentStock: number | null;
  currentThreshold: number | null;
  onConfirm: (stockQuantity: number | null, lowStockThreshold: number | null) => Promise<void>;
  onClose: () => void;
}

export function StockUpdateModal({
  isOpen,
  productName,
  currentStock,
  currentThreshold,
  onConfirm,
  onClose,
}: StockUpdateModalProps) {
  const [stockQuantity, setStockQuantity] = useState<string>(
    currentStock !== null ? currentStock.toString() : '',
  );
  const [lowStockThreshold, setLowStockThreshold] = useState<string>(
    (currentThreshold !== null ? currentThreshold : 10).toString(),
  );
  const [isUnlimited, setIsUnlimited] = useState(currentStock === null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const qty = stockQuantity === '' ? NaN : Math.floor(Number(stockQuantity));
    const threshold = lowStockThreshold === '' ? 10 : Math.floor(Number(lowStockThreshold));
    if (!isUnlimited && (isNaN(qty) || qty < 0)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(
        isUnlimited ? null : qty,
        isUnlimited ? null : Math.max(0, threshold) || 10,
      );
      onClose();
    } catch (error) {
      console.error('Failed to update stock:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <button
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Modal kapat"
      />
      <Card className="relative z-[10000] w-full max-w-md p-0 overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Stok Güncelle</h3>
          <p className="text-gray-600 mb-6">{productName}</p>

          <div className="space-y-4">
            <div>
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={isUnlimited}
                  onChange={(e) => setIsUnlimited(e.target.checked)}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="text-sm font-medium text-gray-700">Sınırsız Stok</span>
              </label>
            </div>

            {!isUnlimited && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stok Miktarı (adet) *
                  </label>
                  <NumberInput
                    integerOnly
                    value={stockQuantity}
                    onChange={setStockQuantity}
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Düşük Stok Uyarı Eşiği (adet)
                  </label>
                  <NumberInput
                    integerOnly
                    value={lowStockThreshold}
                    onChange={setLowStockThreshold}
                    placeholder="10"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Stok bu değerin altına düştüğünde uyarı verilir
                  </p>
                </div>
              </>
            )}

            {currentStock !== null && !isUnlimited && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Mevcut Stok:</span> {currentStock} adet
                </p>
                {currentThreshold !== null && (
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-medium">Mevcut Eşik:</span> {currentThreshold} adet
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6 justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Vazgeç
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirm}
              disabled={isSubmitting || (!isUnlimited && (!stockQuantity || isNaN(Number(stockQuantity))))}
              isLoading={isSubmitting}
            >
              Güncelle
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
